import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import SEO from "@/components/SEO";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";

/** Wraps private routes (auth, checkout, dashboards, admin) with noindex meta. */
const Private = ({ title, children }) => (
  <>
    <SEO noindex title={title} description="Private area — sign-in required." />
    {children}
  </>
);
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Offerings from "@/pages/Offerings";
import OfferingDetail from "@/pages/OfferingDetail";
import CoursePlayer, { QuizPlayer } from "@/pages/CoursePlayer";
import { AdminQuizList, AdminQuizEditor } from "@/pages/AdminQuizzes";
import AdminSubmissions from "@/pages/AdminSubmissions";
import AdminProgress from "@/pages/AdminProgress";
import AdminPageImages from "@/pages/AdminPageImages";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import { StudentDashboard, StudentEvaluationForm, SelfEvaluationPage, TeachingHoursPage, MyStudentEvaluationsPage } from "@/pages/StudentDashboard";
import About from "@/pages/About";
import Aviana from "@/pages/Aviana";
import BookingSuccess from "@/pages/BookingSuccess";
import ReikiBooking from "@/pages/ReikiBooking";
import ReikiBookingSuccess from "@/pages/ReikiBookingSuccess";
import AdminReikiBookings from "@/pages/AdminReikiBookings";
import Resources from "@/pages/Resources";
import Healing from "@/pages/Healing";
import RestorativeAerialYTT from "@/pages/RestorativeAerialYTT";
import RegisterEvent from "@/pages/RegisterEvent";
import AerialYTT from "@/pages/AerialYTT";
import AerialYTTColumbiaMD from "@/pages/AerialYTTColumbiaMD";
import SolsticeRetreat from "@/pages/SolsticeRetreat";
import HostTraining from "@/pages/HostTraining";
import EnrollSuccess from "@/pages/EnrollSuccess";
import BecomeTrainer from "@/pages/BecomeTrainer";
import AdminTrainerApplications from "@/pages/AdminTrainerApplications";
import Contact from "@/pages/Contact";
import RegisterTraining from "@/pages/RegisterTraining";
import TrainingRegistrationConfirmed from "@/pages/TrainingRegistrationConfirmed";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RegisterRetreat from "@/pages/RegisterRetreat";
import PaymentSelect from "@/pages/PaymentSelect";
import PaymentManualConfirmation from "@/pages/PaymentManualConfirmation";
import PaymentSelectEvent from "@/pages/PaymentSelectEvent";
import PaymentEventManualConfirmation from "@/pages/PaymentEventManualConfirmation";
import StripeSuccess from "@/pages/StripeSuccess";
import BalanceCheckout from "@/pages/BalanceCheckout";
import DestinationRetreats from "@/pages/DestinationRetreats";
import WinterSolsticeRetreat from "@/pages/WinterSolsticeRetreat";
import Admin from "@/pages/Admin";
import SoarWithMe from "@/pages/SoarWithMe";
import { BlogList, BlogPost } from "@/pages/Blog";
import CookieBanner from "@/components/CookieBanner";
import AnalyticsLoader from "@/components/AnalyticsLoader";
import MetaPixelTracker from "@/components/MetaPixelTracker";

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
      <HelmetProvider>
        <BrowserRouter>
        <ScrollToTop />
        <MetaPixelTracker />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1">
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/retreats" element={<DestinationRetreats />} />
              <Route path="/walk-with-me" element={<Offerings />} />
              <Route path="/offerings" element={<Offerings />} />
              <Route path="/retreats/solstice-in-the-saddle" element={<SolsticeRetreat />} />
              <Route path="/retreats/saddle-and-soul" element={<Navigate to="/retreats" replace />} />
              <Route path="/retreats/winter-solstice-retreat" element={<WinterSolsticeRetreat />} />
              <Route path="/trainings" element={<Offerings />} />
              <Route path="/trainings/aerial-yoga" element={<AerialYTT />} />
              <Route path="/aerial-yoga-teacher-training-columbia-md" element={<AerialYTTColumbiaMD />} />
              <Route path="/courses" element={<Offerings />} />
              <Route path="/events" element={<Offerings />} />
              <Route path="/offering/:slug" element={<OfferingDetail />} />
              <Route path="/register/:slug" element={<Private title="Training Registration"><RegisterTraining /></Private>} />
              <Route path="/registration/:regId/confirmed" element={<Private title="Registration Confirmed"><TrainingRegistrationConfirmed /></Private>} />
              <Route path="/register-retreat/:slug" element={<Private title="Retreat Registration"><RegisterRetreat /></Private>} />
              <Route path="/register-event/:slug" element={<Private title="Event Registration"><RegisterEvent /></Private>} />
              <Route path="/payment-event/:regId" element={<Private title="Choose Payment"><PaymentSelectEvent /></Private>} />
              <Route path="/payment-event/:regId/manual" element={<Private title="Payment Submitted"><PaymentEventManualConfirmation /></Private>} />
              <Route path="/payment/:regId" element={<Private title="Payment"><PaymentSelect /></Private>} />
              <Route path="/payment/:regId/balance" element={<Private title="Balance Payment"><BalanceCheckout /></Private>} />
              <Route path="/payment/:regId/manual" element={<Private title="Payment Submitted"><PaymentManualConfirmation /></Private>} />
              <Route path="/payment/:regId/success" element={<Private title="Payment Success"><StripeSuccess /></Private>} />
              <Route path="/courses/restorative-aerial-yoga-teacher-training" element={<RestorativeAerialYTT />} />
              {/* Legacy URL that appeared in sitemaps/social shares before Feb 2026 */}
              <Route path="/courses/online-restorative-aerial-yoga-teacher-training" element={<Navigate to="/courses/restorative-aerial-yoga-teacher-training" replace />} />
              <Route path="/courses/:slug/learn" element={<Private title="Course Player"><CoursePlayer /></Private>} />
              <Route path="/courses/:slug/quiz/:quizId" element={<Private title="Quiz"><QuizPlayer /></Private>} />
              <Route path="/admin/lms/quizzes" element={<Private title="Admin · Quizzes"><AdminQuizList /></Private>} />
              <Route path="/admin/lms/quiz/:quizId" element={<Private title="Admin · Quiz Editor"><AdminQuizEditor /></Private>} />
              <Route path="/admin/lms/submissions" element={<Private title="Admin · Submissions"><AdminSubmissions /></Private>} />
              <Route path="/admin/lms/progress" element={<Private title="Admin · Progress"><AdminProgress /></Private>} />
              <Route path="/admin/page-images" element={<Private title="Admin · Page Images"><AdminPageImages /></Private>} />
              <Route path="/admin/trainer-applications" element={<Private title="Admin · Trainer Applications"><AdminTrainerApplications /></Private>} />
              <Route path="/enroll/success" element={<EnrollSuccess />} />
              <Route path="/booking/success" element={<Private title="Booking Confirmed"><BookingSuccess /></Private>} />
              <Route path="/booking/:id/confirmed" element={<Private title="Booking Confirmed"><BookingSuccess /></Private>} />
              <Route path="/reiki" element={<ReikiBooking />} />
              <Route path="/reiki/success" element={<ReikiBookingSuccess />} />
              <Route path="/admin/reiki" element={<Private title="Admin · Reiki"><AdminReikiBookings /></Private>} />
              <Route path="/login" element={<Private title="Sign In"><Login /></Private>} />
              <Route path="/forgot-password" element={<Private title="Reset Password"><ForgotPassword /></Private>} />
              <Route path="/reset-password" element={<Private title="Set New Password"><ResetPassword /></Private>} />
              <Route path="/signup" element={<Private title="Create Account"><Signup /></Private>} />
              <Route path="/dashboard" element={<Private title="Dashboard"><StudentDashboard /></Private>} />
              <Route path="/dashboard/bookings" element={<Private title="My Bookings"><Dashboard /></Private>} />
              <Route path="/dashboard/student-evaluations" element={<Private title="Student Evaluations"><MyStudentEvaluationsPage /></Private>} />
              <Route path="/dashboard/self-evaluations" element={<Private title="Self Evaluations"><SelfEvaluationPage /></Private>} />
              <Route path="/dashboard/teaching-hours" element={<Private title="Teaching Hours"><TeachingHoursPage /></Private>} />
              <Route path="/evaluate/:instructorId" element={<Private title="Student Evaluation"><StudentEvaluationForm /></Private>} />
              <Route path="/about" element={<About />} />
              <Route path="/aviana" element={<Aviana />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/healing" element={<Healing />} />
              <Route path="/host-a-training" element={<HostTraining />} />
              <Route path="/become-an-aerial-yoga-teacher-trainer" element={<BecomeTrainer />} />
              <Route path="/contact" element={<Contact />} />
              {/* ─── Hidden page — referenced only inside Holly's book. Not in sitemap, not linked from nav. ─── */}
              <Route path="/soarwithme" element={<SoarWithMe />} />
              <Route path="/admin" element={<Private title="Admin"><Admin /></Private>} />
              <Route path="/admin/:section" element={<Private title="Admin"><Admin /></Private>} />
              <Route path="/journal" element={<BlogList />} />
              <Route path="/journal/:slug" element={<BlogPost />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
          <Toaster position="bottom-right" />
          <CookieBanner />
          <AnalyticsLoader />
        </div>
      </BrowserRouter>
      </HelmetProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
